import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

export interface RetryConfig {
  retries: number;
  retryDelay: number;
  retryCount: number;
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  __retryConfig?: RetryConfig;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  retries: 3,
  retryDelay: 1000,
  retryCount: 0,
};

export function shouldRetry(error: AxiosError): boolean {
  // Retry on network errors (no response received) and timeouts
  if (!error.response) {
    return error.code === 'ECONNABORTED' ||
           error.code === 'ETIMEDOUT' ||
           error.code === 'ERR_NETWORK' ||
           error.message === 'Network Error';
  }

  // Retry on server errors (5xx)
  const status = error.response.status;
  return status >= 500 && status < 600;
}

export function getRetryDelay(retryCount: number, baseDelay: number = 1000): number {
  return baseDelay * Math.pow(2, retryCount);
}

export function getRetryConfig(config: InternalAxiosRequestConfig): RetryConfig {
  return (config as RetryableRequestConfig).__retryConfig || { ...DEFAULT_RETRY_CONFIG };
}

export function setRetryConfig(config: InternalAxiosRequestConfig, retryConfig: RetryConfig): void {
  (config as RetryableRequestConfig).__retryConfig = retryConfig;
}

export function incrementRetryCount(config: InternalAxiosRequestConfig): void {
  const retryConfig = getRetryConfig(config);
  retryConfig.retryCount += 1;
  setRetryConfig(config, retryConfig);
}
